import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthLoginResponse } from '../models/auth.model';
import { SessionStateService } from '../../core/session-state.service';
import { UserService } from './user.service';

export interface UserFilter {
  fullName?: string;
  email?: string;
  parentUser?: string;
  city?: string;
  rol?: number;
}

export interface Pagination {
  pageNumber: number;
  pageSize: number;
  orderBy?: string;
  ascending?: boolean;
}

export interface FilterPaginatedRequest {
  filters: UserFilter;
  pagination: Pagination;
}

export interface FilterPaginatedResponse {
  currentPage: number;
  pageSize: number;
  rowCount: number;
  pageCount: number;
  result: Array<{
    id: string;
    fullName: string;
    email: string;
    timeZone: string;
    language: string;
    rol: string;
    company: string;
    city: string;
    phone: string;
    version: number;
    createdAt: string;
    createdBy: string | null;
    lastUpdatedAt: string;
    lastUpdatedBy: string;
    isDeleted: boolean;
    deletedAt: string | null;
    deletedBy: string | null;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private sessionState: SessionStateService,
    private userService: UserService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /**
   * Authenticates a user with the backend using their Firebase Google ID
   * @param googleId The Firebase user ID
   * @returns Observable with the authentication response
   */
  loginWithGoogleId(googleId: string): Observable<AuthLoginResponse> {
    const url = `${this.apiUrl}/api/Authentication/login`;
    const headers = { 'Content-Type': 'application/json' };
    return this.http
      .post<AuthLoginResponse>(
        url,
        { googleId },
        {
          headers,
          responseType: 'json',
        },
      )
      .pipe(
        tap((response: AuthLoginResponse) => {
          this.sessionState.setSession({
            token: response.token,
            refreshToken: response.refreshToken,
            userId: response.user.id,
            accountId: response.account.id,
            ownerId: response.account.ownerId,
            planId: response.account.planId,
          });
        }),
      );
  }

  /**
   * Calls the refresh-token endpoint with the stored refreshToken
   * @returns Observable with the refresh response
   */
  refreshToken(): Observable<any> {
    const refreshToken = this.sessionState.refreshToken();
    if (!refreshToken) {
      return throwError(
        () => new Error('No refresh token found in session state'),
      );
    }
    const url = `${this.apiUrl}/api/Authentication/refresh-token`;
    const headers = { 'Content-Type': 'application/json' };
    return this.http
      .post<any>(
        url,
        { refreshToken },
        {
          headers,
          responseType: 'json',
        },
      )
      .pipe(
        tap((response: any) => {
          if (response && response.token) {
            this.sessionState.setSession({ token: response.token });
          }
          if (response && response.refreshToken) {
            this.sessionState.setSession({
              refreshToken: response.refreshToken,
            });
          }
        }),
      );
  }

  /**
   * Refreshes the current user profile from the backend to ensure data consistency
   * @returns Observable with the user profile
   */
  refreshUserProfile(): Observable<any> {
    return this.userService.getUserByUserId().pipe(
      tap((user: any) => {
        if (user && user.rol !== undefined) {
          // Normalize role value (some parts of the app expect string uppercase, others number)
          let backendRol = user.rol;

          // If backend returns number but we need consistency, normalize it.
          // In SessionStateService, rol is Signal<string | null>.
          // Actually, looking at NavigationComponent, it expects 'ADMINISTRATOR'.
          if (typeof backendRol === 'number') {
            if (backendRol === 0) {
              backendRol = 'ADMINISTRATOR';
            } else if (backendRol === 2) {
              backendRol = 'AdminBackoffice';
            } else {
              backendRol = 'USER';
            }
          }

          const currentRol = this.sessionState.rol();
          if (currentRol !== backendRol) {
            console.log(
              `Role mismatch detected! Backend: ${backendRol}, Local: ${currentRol}. Synchronizing...`,
            );
            this.sessionState.setSession({
              rol: backendRol,
            });
          }
        }
      }),
    );
  }

  /**
   * Clears the user session and redirects to login
   */
  logout(): void {
    this.sessionState.clearSession();

    // Items used in other components
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.clear();

    // Redirect to login
    if (this.document.location) {
      this.document.location.href = '/login';
    }
  }

  /**
   * Checks if the user is authenticated by verifying the token existence and validity.
   * @returns true if the user has a valid token, false otherwise.
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token && !this.isTokenExpired(token);
  }

  /**
   * Checks if a JWT token is expired.
   * @param token The JWT token to check.
   * @returns true if the token is expired or invalid, false otherwise.
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return true;
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch {
      return true;
    }
  }
}
